import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Window, TextField, Button, Popup, PopupBody, LoadingOverlay, ValidationIndicator, type ValidationStatus } from '@warmancer/ui';
import { authApi, ApiClientError, getErrorMessage, DISCORD_URL, type ForgotPasswordResponse } from '../api/client';
import styles from './AuthPages.module.css';

export function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  const [retryAfter, setRetryAfter] = useState<Date | null>(null);

  // Real-time email validation
  const emailStatus = useMemo((): ValidationStatus => {
    if (!email) return 'idle';
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? 'valid' : 'invalid';
  }, [email]);

  const forgotMutation = useMutation<ForgotPasswordResponse, Error>({
    mutationFn: () => authApi.forgotPasswordWithRateLimit({ email }),
    onSuccess: (data) => {
      if (data?.rate_limited) {
        setRateLimited(true);
        if (data.retry_after) {
          setRetryAfter(new Date(data.retry_after));
        }
      } else {
        setSuccess(true);
      }
    },
    onError: (error: Error) => {
      const message = getErrorMessage(error);
      if (error instanceof ApiClientError && error.field) {
        setErrors({ [error.field]: message });
      } else {
        setErrors({ form: message });
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setRateLimited(false);
    setRetryAfter(null);

    if (!email.trim()) {
      setErrors({ email: 'Email is required' });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrors({ email: 'Please enter a valid email' });
      return;
    }

    forgotMutation.mutate();
  };

  const clearFormError = () => {
    setErrors((prev) => {
      const { form, ...rest } = prev;
      return rest;
    });
  };

  const formatRetryTime = () => {
    if (!retryAfter) return '';
    const now = new Date();
    const diffMs = retryAfter.getTime() - now.getTime();
    const diffHrs = Math.ceil(diffMs / (1000 * 60 * 60));
    if (diffHrs <= 1) return 'less than an hour';
    return `${diffHrs} hours`;
  };

  return (
    <>
      <Window title="Reset Password" maxWidth={400} className={styles.window}>
        <form onSubmit={handleSubmit} className={styles.form}>
          <p className={styles.formDescription}>
            Enter your email address and we'll send you a link to reset your password.
          </p>

          <TextField
            name="email"
            type="email"
            label="Email"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setEmail(e.target.value);
              if (errors.email) setErrors((prev) => { const { email: _, ...rest } = prev; return rest; });
            }}
            error={!!errors.email}
            autoComplete="email"
            autoFocus
            tabIndex={1}
            rightElement={<ValidationIndicator status={errors.email ? 'invalid' : emailStatus} message={errors.email || (emailStatus === 'invalid' ? 'Please enter a valid email' : '')} />}
          />

          <Button
            type="submit"
            disabled={forgotMutation.isPending}
            tabIndex={2}
          >
            Submit
          </Button>

          <Button
            type="button"
            variant="cancel"
            onClick={() => navigate('/login')}
            tabIndex={3}
          >
            Cancel
          </Button>
        </form>
      </Window>

      <Popup
        open={!!errors.form}
        title="Error"
        variant="error"
      >
        <PopupBody
          note={<>Join us on <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer">Discord</a> for help</>}
          buttons={<Button onClick={clearFormError}>Try Again</Button>}
        >
          {errors.form?.split('\n').map((line, i) => <p key={i}>{line}</p>)}
        </PopupBody>
      </Popup>

      <Popup
        open={success}
        title="Check Your Email"
        variant="success"
      >
        <PopupBody
          note={<>Email sent from contact@warmancer.com<br />Join us on <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer">Discord</a> for help</>}
          buttons={<Button onClick={() => navigate('/login')}>Return to Login</Button>}
        >
          <p>If an account exists with <strong>{email}</strong>, we've sent a password reset link.</p>
          <p>Please check your inbox <strong>and spam folder</strong>.</p>
        </PopupBody>
      </Popup>

      <Popup
        open={rateLimited}
        title="Please Wait"
        variant="info"
      >
        <PopupBody
          note={<>Email sent from contact@warmancer.com<br />Join us on <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer">Discord</a> for help</>}
          buttons={<Button onClick={() => setRateLimited(false)}>OK</Button>}
        >
          <p>You've recently requested a password reset.</p>
          <p>Please check your email (including spam folder) or wait {formatRetryTime() || '24 hours'} before requesting another reset.</p>
        </PopupBody>
      </Popup>

      <LoadingOverlay
        visible={forgotMutation.isPending}
        message="Sending reset link..."
        timeout={10000}
      />
    </>
  );
}
